<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.report_stock_value.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-end">
            <div class="col-md-4">
              <label class="form-control-label">{{ $t("modern_trade.report_stock_value.company") }}</label>
              <el-select v-model="companyFilter" :placeholder="$t('all') || 'All'" clearable class="w-100" @change="loadData">
                <el-option
                  v-for="c in companies"
                  :key="c.id"
                  :label="c.name"
                  :value="c.id"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <base-button size="sm" type="primary" @click="loadData">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
            <div class="col-md-6 text-right">
              <base-button size="sm" type="success" :loading="exporting" @click="exportXlsx">
                <i class="fa fa-file-excel"></i> {{ $t("modern_trade.report_stock_value.export_xlsx") }}
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table :data="rows" row-key="company_id" header-row-class-name="thead-light">
            <el-table-column type="expand">
              <template slot-scope="{ row }">
                <div class="px-4 py-2">
                  <strong class="text-muted small">{{ $t("modern_trade.report_stock_value.drill_down") }}</strong>
                  <table class="table table-sm table-bordered mt-2" v-if="row.products && row.products.length">
                    <thead>
                      <tr>
                        <th>{{ $t("modern_trade.report_stock_value.product") }}</th>
                        <th>{{ $t("modern_trade.report_stock_value.lot_no") }}</th>
                        <th>{{ $t("modern_trade.report_stock_value.expired_date") }}</th>
                        <th class="text-right">{{ $t("modern_trade.report_stock_value.qty") }}</th>
                        <th class="text-right">{{ $t("modern_trade.report_stock_value.unit_price") }}</th>
                        <th class="text-right">{{ $t("modern_trade.report_stock_value.value") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, idx) in row.products" :key="idx" :class="{ 'table-warning': p.no_price }">
                        <td>
                          {{ p.product_name || p.product_code }}
                          <span v-if="p.no_price" class="badge badge-warning ml-1">
                            {{ $t("modern_trade.report_stock_value.no_price") }}
                          </span>
                        </td>
                        <td>{{ p.lot_no || "-" }}</td>
                        <td>{{ p.expired_date || "-" }}</td>
                        <td class="text-right">{{ p.qty }}</td>
                        <td class="text-right">{{ formatMoney(p.unit_price) }}</td>
                        <td class="text-right">{{ formatMoney(p.value) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-else class="text-muted small mb-0">{{ $t("modern_trade.report_stock_value.no_data") }}</p>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_stock_value.company')" min-width="200">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_stock_value.qty')" min-width="100" align="right">
              <template slot-scope="{ row }">{{ row.total_qty !== undefined ? row.total_qty : "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_stock_value.total_value')" min-width="150" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.total_value) }}</template>
            </el-table-column>
          </el-table>
          <p v-if="!loading && rows.length === 0" class="text-muted text-center py-4 mb-0">
            {{ $t("modern_trade.report_stock_value.no_data") }}
          </p>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-end">
          <strong class="mr-2">{{ $t("modern_trade.report_stock_value.total_value") }}:</strong>
          <span>{{ formatMoney(grandTotal) }}</span>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtStockValueReport",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
    }),
    grandTotal() {
      return this.rows.reduce((sum, r) => sum + (Number(r.total_value) || 0), 0);
    },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      companies: [],
      companyFilter: null,
      rows: [],
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    buildParams() {
      const params = {};
      if (this.companyFilter) params.company_id = this.companyFilter;
      return params;
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await ModernTradeService.reportStockValue(this.buildParams());
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || d.companies || [];
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    async exportXlsx() {
      this.exporting = true;
      try {
        const res = await ModernTradeService.reportStockValueExport(this.buildParams());
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mt_stock_value_report.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.exporting = false;
      }
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      return (err.response && err.response.data && err.response.data.Cause) || err.message;
    },
  },
};
</script>
